import { motion } from 'framer-motion';
import data from '../data/data.json';

const PILLARS = [
  {
    icon: '🏥',
    title: 'Purpose-Driven Systems',
    desc: 'Engineering software designed for real human impact—specifically across healthcare workflows, patient journeys, and clinical utility.',
  },
  {
    icon: '⚡',
    title: 'Biosignals & Edge Sensing',
    desc: 'Connecting embedded hardware (ESP32, AD5933) and signal processing pipelines to detect trends before symptoms surface.',
  },
  {
    icon: '✨',
    title: 'Human-Centered Experience',
    desc: 'Bridging the gap between complex algorithms and intuitive, accessible interfaces that empower people.',
  },
];

export default function About() {
  const { about } = data;

  return (
    <section
      id="about"
      className="section"
      style={{
        background: 'linear-gradient(180deg, rgba(250,246,238,0.4) 0%, rgba(247,190,161,0.12) 50%, rgba(250,246,238,0.4) 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <style>{`
        .about-grid {
          display: grid;
          grid-template-columns: 1.1fr 0.9fr;
          gap: 4.5rem;
          align-items: start;
        }
        @media (max-width: 960px) {
          .about-grid {
            grid-template-columns: 1fr !important;
            gap: 3rem !important;
          }
        }
      `}</style>

      <div className="container">
        <div className="about-grid">
          {/* Left Column: Narrative & Bio */}
          <div>
            <motion.p
              className="section-label"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Perspective & Philosophy
            </motion.p>
            <motion.h2
              className="section-title foil-shine"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              style={{ marginBottom: '2rem' }}
            >
              Building with intent.
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              style={{
                fontSize: '1.25rem',
                fontFamily: 'var(--font-heading)',
                fontStyle: 'italic',
                lineHeight: 1.75,
                color: 'var(--text)',
                marginBottom: '1.8rem',
              }}
            >
              "{about.bio}"
            </motion.p>

            {/* Academic Credential Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              style={{
                background: 'rgba(255, 255, 255, 0.75)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid var(--border)',
                borderRadius: '20px',
                padding: '1.5rem 1.8rem',
                marginBottom: '2rem',
                boxShadow: '0 4px 16px rgba(45, 23, 83, 0.04)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.4rem' }}>
                <span style={{ fontSize: '1.2rem' }}>🎓</span>
                <h4 style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--text)' }}>
                  VIT University · Vellore, India
                </h4>
              </div>
              <p style={{ fontSize: '0.88rem', color: 'var(--accent-dark)', fontWeight: 500, marginBottom: '0.2rem' }}>
                Integrated M.Tech in Computer Science & Engineering
              </p>
              <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                Specialized in software engineering, intelligent systems, and healthcare technologies.
              </p>
            </motion.div>

            {/* Current Objective / Callout */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
              style={{
                padding: '1.25rem 1.5rem',
                borderLeft: '3px solid var(--accent)',
                background: 'rgba(232, 149, 109, 0.09)',
                borderRadius: '0 16px 16px 0',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
              }}
            >
              <span style={{ color: 'var(--accent-dark)', fontSize: '1.1rem', marginTop: '0.1rem' }}>✦</span>
              <p style={{ fontSize: '0.94rem', color: 'var(--text-muted)', lineHeight: 1.7 }}>
                {about.goal}
              </p>
            </motion.div>
          </div>

          {/* Right Column: Pillars & Interests Matrix */}
          <div>
            {/* Engineering Pillars */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginBottom: '2.5rem' }}
            >
              {PILLARS.map((pillar, i) => (
                <div
                  key={i}
                  style={{
                    background: 'rgba(255, 255, 255, 0.75)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: '1px solid var(--border)',
                    borderRadius: '20px',
                    padding: '1.5rem',
                    transition: 'all 0.25s ease',
                    boxShadow: '0 4px 16px rgba(45, 23, 83, 0.04)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'var(--accent)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(232, 149, 109, 0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'var(--border)';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 16px rgba(45, 23, 83, 0.04)';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.45rem' }}>
                    <span style={{ fontSize: '1.3rem' }}>{pillar.icon}</span>
                    <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.05rem', fontWeight: 600, color: 'var(--text)' }}>
                      {pillar.title}
                    </h3>
                  </div>
                  <p style={{ fontSize: '0.88rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                    {pillar.desc}
                  </p>
                </div>
              ))}
            </motion.div>

            {/* Interests Matrix */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.35 }}
              style={{
                background: 'rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                border: '1px solid var(--border)',
                borderRadius: '20px',
                padding: '1.5rem',
              }}
            >
              <p
                style={{
                  fontSize: '0.78rem',
                  color: 'var(--accent-dark)',
                  marginBottom: '1rem',
                  fontWeight: 700,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                }}
              >
                Focus Areas & Curiosity
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.55rem' }}>
                {about.interests.map((interest) => (
                  <span
                    key={interest}
                    style={{
                      padding: '0.45rem 1.05rem',
                      borderRadius: '100px',
                      border: '1px solid var(--border)',
                      fontSize: '0.84rem',
                      fontWeight: 500,
                      color: 'var(--text)',
                      background: 'rgba(250, 246, 238, 0.85)',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--accent)';
                      e.currentTarget.style.color = 'var(--accent-dark)';
                      e.currentTarget.style.transform = 'translateY(-1px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--border)';
                      e.currentTarget.style.color = 'var(--text)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    {interest}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
